<script setup>
import {onMounted, ref} from "vue";
import {createFormData} from "@/csrf";
import {mdiAccountInjuryOutline, mdiCloseCircle, mdiPlaylistCheck} from "@mdi/js";
import {useRouter} from "vue-router";

const router = useRouter();

const LoginState = {
    INIT: 'init',
    LOADING: 'loading',
    NOT_AUTHORIZED: 'not_authorized',
    HPO_LIST: 'hpo_list',
    NO_ACTIVE_HPO: 'no_active_hpo',
    ERROR: 'error',
    COMPLETE: 'complete',
}

const state = ref(LoginState.INIT);
const deviceName = ref('');
const hpoList = ref([]);
const coords = ref({
    latitude: null,
    longitude: null,
});

function getCurrentLocation() {
    if (!navigator.geolocation) {
        startLoginFlow();
        return;
    }

    const options = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
    }
    navigator.geolocation.getCurrentPosition(
        position => {
            coords.value = {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
            };
            startLoginFlow();
        },
        error => {
            console.warn(`Geolocation error: ${error.code} - ${error.message}`);
            startLoginFlow();
        },
        options,
    );
}

function startLoginFlow(hpoId = null) {
    const data = createFormData();
    if (coords.value.latitude !== null && coords.value.longitude !== null) {
        data.set('latitude', coords.value.latitude);
        data.set('longitude', coords.value.longitude);
    }
    if (hpoId !== null) {
        data.set('hpo_id', hpoId);
    }

    const request = new Request(reverseUrl('web:device:login_api'), {
        method: 'POST',
        body: data,
    });

    state.value = LoginState.LOADING;
    fetch(request)
        .then(async response => {
            const data = await response.json();
            if (!response.ok) {
                throw {
                    errorCode: data.code,
                    data: data,
                };
            }

            return data;
        })
        .then(data => {
            if (data.device_name) {
                deviceName.value = data.device_name;
            }

            switch (data.code) {
                case 'hpo_list':
                    state.value = LoginState.HPO_LIST;
                    hpoList.value = data.hpo_list;
                    break;
                case 'no_active_hpo':
                    state.value = LoginState.NO_ACTIVE_HPO;
                    break;
                case 'redirect':
                    state.value = LoginState.COMPLETE;
                    router.push(data.location);
                    break;
            }
        })
        .catch(error => {
            if (error.errorCode) {
                switch (error.errorCode) {
                    case 'missing_token':
                    case 'invalid_token':
                        state.value = LoginState.NOT_AUTHORIZED;
                        break;

                    default:
                        state.value = LoginState.ERROR;
                }
                return;
            }

            state.value = LoginState.ERROR;
            console.error(error);
        });
}

function selectHpo({id}) {
    startLoginFlow(id);
}

onMounted(() => {
    getCurrentLocation();
});

function restartLogin() {
    startLoginFlow();
}
</script>

<template>
    <v-card max-width="640" elevation="8" class="flex-fill my-10">
        <v-card-title class="d-flex justify-space-between">
            <span>
                {{ pgettext('title', 'Device login') }}
            </span>
            <strong v-if="deviceName">
                {{ deviceName }}
            </strong>
        </v-card-title>
        <v-divider/>
        <v-card-text>
            <template v-if="state === LoginState.INIT">
                <v-empty-state>
                    <template #media>
                        <v-progress-circular indeterminate :size="48" :width="6" color="blue"/>
                    </template>
                    <template #title>
                        {{ gettext('Getting GPS location of the device.') }}<br />
                        {{ gettext('Please allow access to location services.')}}
                    </template>
                </v-empty-state>
            </template>
            <template v-else-if="state === LoginState.LOADING">
                <v-empty-state>
                    <template #media>
                        <v-progress-circular indeterminate :size="48" :width="6" color="green"/>
                    </template>
                    <template #title>
                        {{ gettext('Logging in') }}
                    </template>
                </v-empty-state>
            </template>
            <template v-else-if="state === LoginState.NOT_AUTHORIZED">
                <v-empty-state :icon="mdiCloseCircle"
                               color="red"
                               :title="pgettext('title', 'This device is not authorized.')"/>
            </template>
            <template v-else-if="state === LoginState.HPO_LIST">
                <v-card>
                    <v-list :items="hpoList" item-title="name" item-value="id" @click:select="selectHpo" />
                </v-card>
            </template>
            <template v-else-if="state === LoginState.NO_ACTIVE_HPO">
                <v-empty-state :icon="mdiPlaylistCheck"
                               color="green"
                               :title="pgettext('title', 'There is no active HPO.')"/>
            </template>
            <template v-else-if="state === LoginState.ERROR">
                <v-empty-state :icon="mdiCloseCircle"
                               color="red"
                               :title="pgettext('title', 'Error while logging in. Try again later.')"/>
            </template>
            <template v-else-if="state === LoginState.COMPLETE">
                <v-empty-state :icon="mdiAccountInjuryOutline"
                               color="blue"
                               :title="pgettext('title', 'Please log-in again to continue.')"
                               :action-text="pgettext('action', 'Login Again')"
                               @click:action="startLoginFlow()"/>
            </template>
        </v-card-text>
    </v-card>
</template>
